import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Flex,
  Grid,
  Link,
  Image,
  HStack,
  Text,
  Heading,
  Divider,
  LinkProps,
  Input,
  InputRightElement,
  InputGroup,
} from "@chakra-ui/react";
import NextLink from "next/link";
import TitleDefault from "../../TitleDefault";
import { motion } from "framer-motion";
import { ArrowForwardIcon, SearchIcon } from "@chakra-ui/icons";
import RichText from "../../RichText";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { useQuery } from "react-query";
import styled from "@emotion/styled/types/base";
import { filtrarFaq } from "../../../lib/products";
import { useMediaQuery } from "react-responsive";

const MotionLink = motion<LinkProps>(Link);

interface ILink {
  id: number;
  link: string;
  rotulo: string;
}

interface IFaqCategory {
  id: number;
  nome: string;
  slug: string;
}

interface FaqProps {
  id: number;
  pergunta: string;
  resposta: string;
  faq_categoria: IFaqCategory;
  link: ILink;
}

interface Props {
  initialData: FaqProps[];
}

export default function Faq({ initialData }: Props) {
  const isSmallerThan768 = useMediaQuery({ query: "(max-width: 768px)" });
  // const [templateColumns, setTemplateColumns] = useState("repeat(2, 1fr)");
  const [slidesToShow, setSlidesToShow] = useState(3);

  let settings = {
    dots: false,
    infinite: true,
    arrows: false,
    speed: 500,
    slidesToScroll: 1,
  };
  const sliderRef = useRef<any>(null);
  const [search, setSearch] = useState<String>("");

  useEffect(() => {
    if (isSmallerThan768) {
      setSlidesToShow(1);
    } else {
      setSlidesToShow(3);
    }
  }, [isSmallerThan768]);

  // React-query
  const { data, isLoading, isError } = useQuery(
    ["faq", { filtro: search }],
    filtrarFaq,
    {
      initialData: { faq: initialData },
    }
  );

  return (
    <Flex id="faq" w="full" mb={10}>
      <Flex flexDir="column" w="full">
        <Flex>
          <Flex flexDir="column" w="full">
            <Text
              color="#017DC5"
              fontSize="14px"
              textTransform="uppercase"
              mb={4}
              w="full"
            >
              FAQ
            </Text>

            <Heading
              as="h2"
              fontSize={["1.4em", "1.4em", "1.4em", "40px"]}
              color="#101010"
              w="full"
            >
              Confira as dúvidas frequentes
            </Heading>
            <Divider
              bgColor="#ED1B2F"
              height={1}
              borderRadius={4}
              maxW="40px"
              mt={4}
              mb={8}
            />
          </Flex>

          {data?.faq.length > 3 && (
            <Flex flex="1" justifyContent="flex-end" w={["10%"]}>
              <HStack>
                <ChevronLeftIcon
                  onClick={() => {
                    sliderRef.current.slickPrev();
                  }}
                  color="#007DC5"
                  fontSize="3em"
                  cursor="pointer"
                  opacity={0.5}
                  _hover={{ opacity: 1 }}
                />
                <ChevronRightIcon
                  onClick={() => sliderRef.current.slickNext()}
                  color="#007DC5"
                  fontSize="3em"
                  cursor="pointer"
                  opacity={0.5}
                  _hover={{ opacity: 1 }}
                />
              </HStack>
            </Flex>
          )}
        </Flex>

        <Box w="full" maxW="350px" mb={14}>
          <InputGroup>
            <Input
              borderWidth="1px"
              color="#555"
              bgColor="white"
              type="search"
              placeholder="Tem alguma dúvida? Busque aqui"
              _placeholder={{ color: "#555" }}
              _focus={{ borderColor: "#555" }}
              _hover={{ borderColor: "#e4e4e4" }}
              onChange={(e) => setSearch(e.target.value)}
            />
            <InputRightElement children={<SearchIcon color="#ED1B2F" />} />
          </InputGroup>
        </Box>
        {data?.faq.length > 3 ? (
          <Slider ref={sliderRef} slidesToShow={slidesToShow} {...settings}>
            {data?.faq &&
              data?.faq.map((item: FaqProps) => (
                <Flex key={item.id} px={2}>
                  <Box
                    key={item.id}
                    as="article"
                    w="full"
                    bg="white"
                    boxShadow="4px 4px 4px rgba(0, 0, 0, 0.05)"
                    borderRadius="20px"
                    overflow="hidden"
                    p={6}
                  >
                    <HStack mb={4}>
                      {item.faq_categoria?.nome && (
                        <Text
                          fontSize="14px"
                          color="#00377B"
                          textTransform="uppercase"
                          fontWeight="medium"
                        >
                          {item.faq_categoria?.nome}
                        </Text>
                      )}
                    </HStack>
                    <Heading as="h4" fontSize="24px">
                      {item.pergunta}
                    </Heading>
                    <Divider
                      bgColor="#ED1B2F"
                      height={1}
                      borderRadius={4}
                      w="15%"
                      my={3}
                    />
                    {item.resposta && <RichText text={item.resposta} />}
                    {item.link && (
                      <NextLink href={item.link?.link} passHref>
                        <MotionLink
                          color="#00377B"
                          fontWeight="semibold"
                          fontSize="14px"
                          display="block"
                          textAlign="left"
                          py={2}
                          mt={8}
                          _focus={{ outline: "none" }}
                          whileHover={{ x: 5 }}
                        >
                          {item.link?.rotulo} <ArrowForwardIcon />
                        </MotionLink>
                      </NextLink>
                    )}
                  </Box>
                </Flex>
              ))}
          </Slider>
        ) : (
          <Grid
            as="section"
            templateColumns={[
              "repeat(1, 1fr)",
              "repeat(1, 1fr)",
              "repeat(1, 1fr)",
              "repeat(3, 1fr)",
            ]}
            gap={8}
            mt={4}
            mb={[8, 8, 8, 0]}
          >
            {data?.faq &&
              data?.faq.map((item: FaqProps) => (
                <Box
                  key={item.id}
                  as="article"
                  w="full"
                  bg="white"
                  boxShadow="4px 4px 4px rgba(0, 0, 0, 0.05)"
                  borderRadius="20px"
                  overflow="hidden"
                >
                  <Box p={6}>
                    <HStack mb={4}>
                      {item.faq_categoria?.nome && (
                        <Text
                          fontSize="14px"
                          color="#00377B"
                          textTransform="uppercase"
                          fontWeight="medium"
                        >
                          {item.faq_categoria?.nome}
                        </Text>
                      )}
                    </HStack>
                    <Heading as="h4" fontSize="24px">
                      {item.pergunta}
                    </Heading>
                    <Divider
                      bgColor="#ED1B2F"
                      height={1}
                      borderRadius={4}
                      w="15%"
                      my={3}
                    />
                    {item.resposta && <RichText text={item.resposta} />}
                    {item.link && (
                      <NextLink href={item.link?.link} passHref>
                        <MotionLink
                          color="#00377B"
                          fontWeight="semibold"
                          fontSize="14px"
                          display="block"
                          textAlign="left"
                          py={2}
                          mt={8}
                          _focus={{ outline: "none" }}
                          whileHover={{ x: 5 }}
                        >
                          {item.link?.rotulo} <ArrowForwardIcon />
                        </MotionLink>
                      </NextLink>
                    )}
                  </Box>
                </Box>
              ))}
          </Grid>
        )}
      </Flex>
    </Flex>
  );
}
